import React from 'react';
import { CheckCircle2, AlertTriangle, ArrowRight, Plus } from 'lucide-react';
import { CopyableText } from '../../../../../../components/ui/CopyableText';

/**
 * What was created, and the credentials that came back with it.
 *
 * The temporary password is shown rather than discarded — this response is the
 * only place it is ever readable. The mail carrying it can fail without failing
 * the creation, and when it does the seller is left with an account it cannot
 * reach and nobody the wiser, so that case gets said out loud.
 *
 * @param {object} props
 * @param {{user_email: string, user_temp_password: string, company_id: number,
 *   warehouses: number[], notification_email: boolean}} props.result
 * @param {() => void} props.onViewSeller
 * @param {() => void} props.onCreateAnother
 */
export const SellerCreatedPanel = ({ result, onViewSeller, onCreateAnother }) => {
  const emailFailed = result.notification_email === false;

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-in fade-in zoom-in-95 duration-300">
      <div className="text-center space-y-3">
        <div className="w-14 h-14 rounded-2xl bg-ninja-greenLight text-ninja-green flex items-center justify-center mx-auto">
          <CheckCircle2 size={28} />
        </div>
        <h1 className="text-2xl font-black text-ninja-dark dark:text-white">Seller creado</h1>
        <p className="text-text-secondary">
          Se creó la empresa, su cuenta de acceso y {result.warehouses?.length || 0}{' '}
          {result.warehouses?.length === 1 ? 'bodega' : 'bodegas'}.
        </p>
      </div>

      {emailFailed ? (
        <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-900 text-amber-800 dark:text-amber-300">
          <AlertTriangle size={18} className="shrink-0 mt-0.5" />
          <p className="text-sm">
            <span className="font-semibold">No se pudo enviar el correo con las credenciales.</span>{' '}
            Cópialas de aquí y entrégaselas al seller: es la única vez que la contraseña queda a la
            vista.
          </p>
        </div>
      ) : (
        <p className="text-sm text-text-secondary text-center">
          Le enviamos las credenciales por correo. Guárdalas igual: la contraseña no se vuelve a
          mostrar.
        </p>
      )}

      <div className="rounded-2xl border border-border bg-white dark:bg-[#1a1a1a] divide-y divide-border">
        <div className="flex items-center justify-between gap-4 px-5 py-4">
          <span className="text-sm text-text-secondary shrink-0">Usuario</span>
          <CopyableText value={result.user_email} maxWidth="320px" className="font-medium" />
        </div>
        <div className="flex items-center justify-between gap-4 px-5 py-4">
          <span className="text-sm text-text-secondary shrink-0">Contraseña temporal</span>
          <CopyableText
            value={result.user_temp_password}
            maxWidth="320px"
            className="font-mono font-semibold"
          />
        </div>
      </div>

      <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
        <button
          type="button"
          onClick={onCreateAnother}
          className="w-full sm:w-auto flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl border border-border text-text-secondary hover:text-ninja-dark dark:hover:text-white hover:border-ninja-dark dark:hover:border-white font-semibold text-sm transition-colors"
        >
          <Plus size={16} />
          Crear otro seller
        </button>
        <button
          type="button"
          onClick={onViewSeller}
          className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl bg-ninja-green hover:bg-ninja-green/90 text-white font-bold text-sm transition-all"
        >
          Ver seller
          <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
};
